function normalizeCoordinate(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeAccuracy(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePositiveNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function toRadians(value) {
  return (value * Math.PI) / 180;
}

export function validateGpsExecutionContext(context) {
  if (!context) {
    return {
      ok: false,
      code: "GPS_CONFIG_MISSING_CONTEXT",
      message: "Contesto GPS non disponibile. Contatta l'amministrazione.",
      details: {},
    };
  }

  const mode = typeof context.mode_localizzazione === "string"
    ? context.mode_localizzazione.trim().toUpperCase()
    : "";

  if (!context.localizzazione) {
    return {
      ok: false,
      code: "GPS_CONFIG_DISABLED",
      message: "La localizzazione non e' abilitata per questa timbrata. Contatta l'amministrazione.",
      details: { mode_localizzazione: mode || null },
    };
  }

  if (!mode) {
    return {
      ok: false,
      code: "GPS_CONFIG_MISSING_MODE",
      message: "Modalita' di localizzazione non configurata. Contatta l'amministrazione.",
      details: {},
    };
  }

  if (mode !== "GPS") {
    return {
      ok: false,
      code: "GPS_CONFIG_UNSUPPORTED_MODE",
      message: "La modalita' di localizzazione configurata non e' supportata per il controllo GPS.",
      details: { mode_localizzazione: mode },
    };
  }

  if (!context.gps_accuracy_max_configured) {
    return {
      ok: false,
      code: "GPS_CONFIG_MISSING_ACCURACY_MAX",
      message: "Parametro d'istanza REACT_APP_GPS_ACCURACY_MAX non configurato. Contatta l'amministrazione.",
      details: { gps_accuracy_max_raw: context.gps_accuracy_max_raw || null },
    };
  }

  if (!context.gps_accuracy_max_valid) {
    return {
      ok: false,
      code: "GPS_CONFIG_INVALID_ACCURACY_MAX",
      message: "Parametro d'istanza REACT_APP_GPS_ACCURACY_MAX non valido. Contatta l'amministrazione.",
      details: { gps_accuracy_max_raw: context.gps_accuracy_max_raw || null },
    };
  }

  if (context.latitudine == null || context.longitudine == null) {
    return {
      ok: false,
      code: "GPS_CONFIG_MISSING_LOCATION_COORDS",
      message: "Configurazione GPS incompleta: coordinate location mancanti. Contatta l'amministrazione.",
      details: {
        latitudine: context.latitudine,
        longitudine: context.longitudine,
      },
    };
  }

  if (context.tolleranza == null) {
    return {
      ok: false,
      code: "GPS_CONFIG_MISSING_TOLERANCE",
      message: "Configurazione GPS incompleta: tolleranza mancante. Contatta l'amministrazione.",
      details: {},
    };
  }

  return {
    ok: true,
    code: null,
    message: null,
    details: {
      gps_accuracy_max: context.gps_accuracy_max,
      tolleranza: normalizePositiveNumber(context.tolleranza),
      mode_localizzazione: mode,
    },
  };
}

export function calculateDistanceMeters(from, to) {
  const lat1 = normalizeCoordinate(from?.latitudine);
  const lon1 = normalizeCoordinate(from?.longitudine);
  const lat2 = normalizeCoordinate(to?.latitudine);
  const lon2 = normalizeCoordinate(to?.longitudine);

  if ([lat1, lon1, lat2, lon2].some((value) => value === null)) {
    throw new Error("Coordinate non valide per il calcolo della distanza.");
  }

  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
}

export function evaluateGpsValidation({
  currentPosition,
  expectedPosition,
  tolleranza,
  accuracy,
  maxUnusableAccuracy,
}) {
  const distance = calculateDistanceMeters(currentPosition, expectedPosition);
  const toleranceValue = normalizePositiveNumber(tolleranza);
  const accuracyValue = normalizeAccuracy(accuracy);
  const maxAccuracyValue = normalizePositiveNumber(maxUnusableAccuracy);

  if (toleranceValue === null) {
    throw new Error("Tolleranza GPS non valida.");
  }

  if (accuracyValue === null) {
    throw new Error("Accuratezza GPS non disponibile.");
  }

  if (maxAccuracyValue === null) {
    throw new Error("Parametro d'istanza GPS non valido.");
  }

  if (accuracyValue > maxAccuracyValue) {
    return {
      result: "INCERTA_RIPROVA",
      reason: "accuracy-too-low",
      distance,
      accuracy: accuracyValue,
      tolerance: toleranceValue,
      maxUnusableAccuracy: maxAccuracyValue,
    };
  }

  if (distance <= toleranceValue) {
    return {
      result: "VALIDA",
      reason: "inside-tolerance",
      distance,
      accuracy: accuracyValue,
      tolerance: toleranceValue,
      maxUnusableAccuracy: maxAccuracyValue,
    };
  }

  if (distance <= toleranceValue + accuracyValue) {
    return {
      result: "INCERTA_RIPROVA",
      reason: "within-accuracy-margin",
      distance,
      accuracy: accuracyValue,
      tolerance: toleranceValue,
      maxUnusableAccuracy: maxAccuracyValue,
    };
  }

  return {
    result: "FUORI_AREA",
    reason: "outside-tolerance",
    distance,
    accuracy: accuracyValue,
    tolerance: toleranceValue,
    maxUnusableAccuracy: maxAccuracyValue,
  };
}

export function getCurrentGpsPosition(options = {}) {
  const {
    enableHighAccuracy = true,
    timeout = 15000,
    maximumAge = 0,
  } = options;

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.reject(new Error("Geolocalizzazione non disponibile sul dispositivo."));
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = position?.coords || {};
        resolve({
          latitudine: normalizeCoordinate(coords.latitude),
          longitudine: normalizeCoordinate(coords.longitude),
          accuracy: normalizeAccuracy(coords.accuracy),
          altitude: normalizeCoordinate(coords.altitude),
          heading: normalizeCoordinate(coords.heading),
          speed: normalizeCoordinate(coords.speed),
          timestamp: position?.timestamp ? new Date(position.timestamp).toISOString() : new Date().toISOString(),
        });
      },
      (error) => {
        if (!error) {
          reject(new Error("Impossibile rilevare la posizione GPS."));
          return;
        }

        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(new Error("Permesso posizione negato. Abilita la geolocalizzazione per continuare."));
            return;
          case error.POSITION_UNAVAILABLE:
            reject(new Error("Posizione non disponibile. Verifica GPS e connettivita' del dispositivo."));
            return;
          case error.TIMEOUT:
            reject(new Error("Tempo scaduto durante il recupero della posizione. Riprova tra qualche secondo."));
            return;
          default:
            reject(new Error("Errore durante il recupero della posizione GPS."));
        }
      },
      { enableHighAccuracy, timeout, maximumAge }
    );
  });
}